import React, { useRef, useEffect, useMemo, useState } from 'react';
import TextField from 'components/TextField';
import InputMask from 'react-input-mask';
import { cteAPI } from 'services/apiRest';
import { locationByAddress } from 'services/getLocations';
import { WrapperInput, SectionTitle, Row, Column, AutocompleteWrapper, AutocompleteDropdown, AutocompleteItem } from '../ModalViagemCompleto/styles';

// Componente para máscara dinâmica de CPF/CNPJ
// Não usa InputMask com máscara fixa, formata manualmente para permitir CPF e CNPJ
const CpfCnpjMaskedInput = ({ value, onChange, onBlur, children, ...props }) => {
  // Função para formatar o valor conforme a máscara
  const handleChange = (e) => {
    const inputValue = e.target.value;
    // Remove tudo exceto números
    const cleanInput = inputValue.replace(/\D/g, '');
    
    // Limita a 14 dígitos (tamanho máximo do CNPJ)
    const limitedValue = cleanInput.substring(0, 14);
    
    // Aplica a máscara apropriada baseado no tamanho
    let formattedValue = '';
    if (limitedValue.length === 0) {
      formattedValue = '';
    } else if (limitedValue.length <= 11) {
      // Formata como CPF: 999.999.999-99
      if (limitedValue.length <= 3) {
        formattedValue = limitedValue;
      } else if (limitedValue.length <= 6) {
        formattedValue = limitedValue.replace(/(\d{3})(\d+)/, '$1.$2');
      } else if (limitedValue.length <= 9) {
        formattedValue = limitedValue.replace(/(\d{3})(\d{3})(\d+)/, '$1.$2.$3');
      } else {
        formattedValue = limitedValue.replace(/(\d{3})(\d{3})(\d{3})(\d+)/, '$1.$2.$3-$4');
      }
    } else {
      // Formata como CNPJ: 99.999.999/9999-99
      if (limitedValue.length <= 2) {
        formattedValue = limitedValue;
      } else if (limitedValue.length <= 5) {
        formattedValue = limitedValue.replace(/(\d{2})(\d+)/, '$1.$2');
      } else if (limitedValue.length <= 8) {
        formattedValue = limitedValue.replace(/(\d{2})(\d{3})(\d+)/, '$1.$2.$3');
      } else if (limitedValue.length <= 12) {
        formattedValue = limitedValue.replace(/(\d{2})(\d{3})(\d{3})(\d+)/, '$1.$2.$3/$4');
      } else {
        formattedValue = limitedValue.replace(/(\d{2})(\d{3})(\d{3})(\d{4})(\d+)/, '$1.$2.$3/$4-$5');
      }
    }
    
    // Cria um novo evento com o valor formatado
    const syntheticEvent = {
      ...e,
      target: {
        ...e.target,
        value: formattedValue
      }
    };
    
    onChange(syntheticEvent);
  };
  
  // Não usa InputMask, apenas formata o valor diretamente no TextField
  return children({
    value: value || '',
    onChange: handleChange,
    onBlur: onBlur,
    ...props
  });
};

// Componente para máscara monetária
// Não usa InputMask, formata manualmente para permitir digitação livre
const MonetaryMaskedInput = ({ value, onChange, onBlur, children, ...props }) => {
  // Formata valor numérico para exibição (1.234,56)
  const formatarParaExibicao = (valor) => {
    if (!valor || valor === '' || valor === '0' || valor === '0.00') return '';
    // Se for string formatada (já tem vírgula), retorna como está
    if (typeof valor === 'string' && valor.includes(',')) {
      return valor;
    }
    // Converte para número
    const num = typeof valor === 'string' 
      ? parseFloat(valor.toString().replace(/[^\d,.-]/g, '').replace(',', '.')) 
      : parseFloat(valor);
    if (isNaN(num) || num === 0) return '';
    return num.toLocaleString('pt-BR', {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    });
  };
  
  const handleChange = (e) => {
    const inputValue = e.target.value;
    
    // Remove tudo exceto números, vírgula e ponto
    let apenasNumeros = inputValue.replace(/[^\d,.]/g, '');
    
    // Remove pontos (separadores de milhar) mas mantém vírgula se existir
    if (apenasNumeros.includes(',')) {
      // Se tem vírgula, remove todos os pontos (são separadores de milhar)
      apenasNumeros = apenasNumeros.replace(/\./g, '');
    } else {
      // Se não tem vírgula, remove todos os pontos
      apenasNumeros = apenasNumeros.replace(/\./g, '');
    }
    
    // Se estiver vazio ou só vírgula, retorna vazio
    if (!apenasNumeros || apenasNumeros === ',') {
      const syntheticEvent = {
        ...e,
        target: {
          ...e.target,
          value: ''
        }
      };
      onChange(syntheticEvent);
      return;
    }
    
    // Converte para número (substitui vírgula por ponto para cálculo)
    const valorNumerico = parseFloat(apenasNumeros.replace(',', '.'));
    
    if (isNaN(valorNumerico) || valorNumerico < 0) {
      const syntheticEvent = {
        ...e,
        target: {
          ...e.target,
          value: ''
        }
      };
      onChange(syntheticEvent);
      return;
    }
    
    // Formata para exibição (1.234,56) - mantém o valor original, apenas formata
    const valorFormatado = valorNumerico.toLocaleString('pt-BR', {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    });
    
    const syntheticEvent = {
      ...e,
      target: {
        ...e.target,
        value: valorFormatado
      }
    };
    
    onChange(syntheticEvent);
  };
  
  // Valor formatado para exibição
  const valorExibicao = formatarParaExibicao(value);
  
  // Não usa InputMask, apenas formata o valor diretamente no TextField
  return children({
    value: valorExibicao || '',
    onChange: handleChange,
    onBlur: onBlur,
    ...props
  });
};

// Componente wrapper para InputMask que preserva a posição do cursor
const MaskedInput = ({ mask, value, onChange, onBlur, children, ...props }) => {
  const handleChange = (e) => {
    const input = e.target;
    const cursorPosition = input.selectionStart;
    const oldValue = value || '';
    const newValue = input.value || '';
    
    // Chama o onChange original
    onChange(e);
    
    // Restaura a posição do cursor após a atualização
    // O InputMask já ajusta a posição, mas precisamos garantir que não vá para o final
    setTimeout(() => {
      if (input && input.setSelectionRange) {
        // Calcula a nova posição considerando a diferença de tamanho
        const lengthDiff = newValue.length - oldValue.length;
        let newPosition = cursorPosition;
        
        // Se adicionou caracteres (máscara), ajusta a posição
        if (lengthDiff > 0) {
          // Conta quantos caracteres de máscara foram adicionados antes da posição do cursor
          const beforeCursor = oldValue.substring(0, cursorPosition);
          const afterCursor = newValue.substring(0, cursorPosition + lengthDiff);
          
          // Ajusta para manter a posição relativa
          newPosition = Math.min(cursorPosition + lengthDiff, newValue.length);
        } else if (lengthDiff < 0) {
          // Se removeu caracteres, mantém a posição relativa
          newPosition = Math.max(0, cursorPosition + lengthDiff);
        }
        
        // Garante que a posição não ultrapasse o tamanho do valor
        newPosition = Math.min(newPosition, newValue.length);
        input.setSelectionRange(newPosition, newPosition);
      }
    }, 0);
  };

  const handleBlur = (e) => {
    console.log('🟡 MaskedInput handleBlur chamado:', e.target.value);
    console.log('🟡 onBlur prop disponível:', typeof onBlur);
    // Chama o onBlur original se fornecido
    if (onBlur) {
      console.log('🟡 Chamando onBlur original...');
      try {
        onBlur(e);
        console.log('🟡 onBlur original executado com sucesso');
      } catch (error) {
        console.error('🟡 Erro ao executar onBlur:', error);
      }
    } else {
      console.warn('🟡 onBlur não foi fornecido!');
    }
  };

  return (
    <InputMask
      {...props}
      mask={mask}
      value={value}
      onChange={handleChange}
      onBlur={handleBlur}
      alwaysShowMask={false}
      maskChar={null}
    >
      {children}
    </InputMask>
  );
};

// Função para buscar CEP usando API Nuvem Fiscal
const buscarCep = async (cepValue, setFieldValue, setValues, values, prefixo) => {
  console.log('=== INÍCIO buscarCep (Nuvem Fiscal) ===');
  console.log('CEP recebido:', cepValue);
  console.log('Prefixo:', prefixo);
  
  // Remove caracteres não numéricos
  const cepLimpo = cepValue.replace(/\D/g, '');
  console.log('CEP limpo:', cepLimpo);
  
  // Verifica se o CEP tem 8 dígitos
  if (cepLimpo.length !== 8) {
    console.log('❌ CEP incompleto:', cepLimpo, 'tamanho:', cepLimpo.length);
    return;
  }

  console.log('🔍 Buscando CEP na API Nuvem Fiscal:', cepLimpo, 'para prefixo:', prefixo);

  try {
    // Consultar CEP na API Nuvem Fiscal
    const cepResponse = await cteAPI.consultarCep(cepLimpo);
    console.log('📦 Dados retornados pela API Nuvem Fiscal:', cepResponse);

    // Preparar objeto com todas as atualizações
    // Mapear campos da API Nuvem Fiscal para campos do formulário
    const updates = {};
    
    // logradouro -> logradouro
    if (cepResponse.logradouro) {
      updates[`${prefixo}_logradouro`] = cepResponse.logradouro;
    }
    
    // bairro -> bairro
    if (cepResponse.bairro) {
      updates[`${prefixo}_bairro`] = cepResponse.bairro;
    }
    
    // municipio -> municipio_descricao
    if (cepResponse.municipio) {
      updates[`${prefixo}_municipio_descricao`] = cepResponse.municipio;
    }
    
    // uf -> uf
    if (cepResponse.uf) {
      updates[`${prefixo}_uf`] = cepResponse.uf;
    }
    
    // codigo_ibge -> municipio_codigo
    if (cepResponse.codigo_ibge) {
      updates[`${prefixo}_municipio_codigo`] = cepResponse.codigo_ibge;
    }
    
    // complemento -> complemento (se o campo existir no formulário)
    if (cepResponse.complemento) {
      updates[`${prefixo}_complemento`] = cepResponse.complemento;
    }
    
    // tipo_logradouro pode ser usado para enriquecer o logradouro se necessário
    if (cepResponse.tipo_logradouro && cepResponse.logradouro) {
      // Se o logradouro não começar com o tipo, adicionar
      if (!cepResponse.logradouro.toLowerCase().startsWith(cepResponse.tipo_logradouro.toLowerCase())) {
        updates[`${prefixo}_logradouro`] = `${cepResponse.tipo_logradouro} ${cepResponse.logradouro}`;
      }
    }
    
    console.log('📝 Atualizações a serem aplicadas:', updates);
    console.log('📝 Valores atuais antes da atualização:', {
      logradouro: values[`${prefixo}_logradouro`],
      bairro: values[`${prefixo}_bairro`],
      municipio: values[`${prefixo}_municipio_descricao`],
      uf: values[`${prefixo}_uf`],
      codigo_ibge: values[`${prefixo}_municipio_codigo`]
    });
    
    // Usar setValues se disponível (mais eficiente para múltiplos campos)
    // IMPORTANTE: usar função callback para garantir que os valores anteriores sejam preservados
    if (setValues && Object.keys(updates).length > 0) {
      console.log('📝 Usando setValues com função callback para atualizar todos os campos');
      setValues((prevValues) => {
        const newValues = {
          ...prevValues,
          ...updates
        };
        console.log('📝 Novos valores após setValues:', {
          logradouro: newValues[`${prefixo}_logradouro`],
          bairro: newValues[`${prefixo}_bairro`],
          municipio: newValues[`${prefixo}_municipio_descricao`],
          uf: newValues[`${prefixo}_uf`],
          codigo_ibge: newValues[`${prefixo}_municipio_codigo`]
        });
        return newValues;
      });
    } else if (setFieldValue) {
      // Fallback: usar setFieldValue individualmente
      console.log('📝 Usando setFieldValue individualmente');
      Object.entries(updates).forEach(([fieldName, fieldValue]) => {
        console.log(`  → setFieldValue('${fieldName}', '${fieldValue}')`);
        setFieldValue(fieldName, fieldValue);
      });
    } else {
      console.error('❌ Nem setValues nem setFieldValue estão disponíveis!');
    }
    
    console.log('✅ Todos os campos foram atualizados!');
    console.log('=== FIM buscarCep ===');
    
    // Após buscar o CEP, tentar geocodificar o endereço
    setTimeout(() => {
      geocodificarEndereco(prefixo, setFieldValue, setValues, values);
    }, 500);
    
  } catch (error) {
    console.error('❌ Erro ao buscar CEP:', error);
    alert('CEP não encontrado. Por favor, verifique o CEP informado.');
  }
};

// Buscar CEP para campos "collect_*" e "delivery_*" (cadastro de viagens)
const buscarCepColetaEntrega = async (cepValue, setValues, prefixo) => {
  const cepLimpo = String(cepValue || '').replace(/\D/g, '');
  if (cepLimpo.length !== 8) return;

  try {
    const cepResponse = await cteAPI.consultarCep(cepLimpo);

    const logradouro =
      (cepResponse.tipo_logradouro && cepResponse.logradouro)
        ? `${cepResponse.tipo_logradouro} ${cepResponse.logradouro}`.trim()
        : (cepResponse.logradouro || '');

    const updates = {};
    if (logradouro) updates[`${prefixo}_point`] = logradouro;
    if (cepResponse.bairro) updates[`${prefixo}_neighborhood`] = cepResponse.bairro;
    if (cepResponse.municipio) updates[`${prefixo}_city`] = cepResponse.municipio;
    if (cepResponse.uf) updates[`${prefixo}_state`] = cepResponse.uf;
    if (cepResponse.complemento) updates[`${prefixo}_complement`] = cepResponse.complemento;

    if (Object.keys(updates).length > 0 && setValues) {
      setValues((prev) => ({ ...prev, ...updates }));
    }
  } catch (error) {
    console.error('❌ Erro ao buscar CEP (coleta/entrega):', error);
    alert('CEP não encontrado. Por favor, verifique o CEP informado.');
  }
};

// Função para geocodificar endereço e obter latitude/longitude
const geocodificarEndereco = async (prefixo, setFieldValue, setValues, values) => {
  try {
    const logradouro = values[`${prefixo}_logradouro`];
    const numero = values[`${prefixo}_numero`];
    const bairro = values[`${prefixo}_bairro`];
    const municipio = values[`${prefixo}_municipio_descricao`];
    const uf = values[`${prefixo}_uf`];
    const cep = values[`${prefixo}_cep`];
    
    // Verificar se temos informações suficientes para geocodificar
    if (!logradouro || !municipio || !uf) {
      console.log(`ℹ️ Informações insuficientes para geocodificar ${prefixo}`);
      return;
    }
    
    // Montar endereço completo de forma mais limpa para geocodificação
    // Formato: "Logradouro, Número - Bairro, Município - UF, CEP, Brasil"
    const partesEndereco = [];
    
    // Logradouro e número juntos
    if (logradouro) {
      if (numero) {
        partesEndereco.push(`${logradouro}, ${numero}`);
      } else {
        partesEndereco.push(logradouro);
      }
    }
    
    // Bairro
    if (bairro) {
      partesEndereco.push(bairro);
    }
    
    // Município e UF juntos
    if (municipio && uf) {
      partesEndereco.push(`${municipio} - ${uf}`);
    } else if (municipio) {
      partesEndereco.push(municipio);
    } else if (uf) {
      partesEndereco.push(uf);
    }
    
    // CEP (opcional, pode ajudar na precisão)
    if (cep) {
      const cepLimpo = cep.replace(/\D/g, '');
      if (cepLimpo.length === 8) {
        partesEndereco.push(cepLimpo);
      }
    }
    
    // Brasil
    partesEndereco.push('Brasil');
    
    const enderecoCompleto = partesEndereco.join(', ');
    
    console.log(`🔍 Geocodificando endereço do ${prefixo}:`, enderecoCompleto);
    
    const coordenadas = await locationByAddress(enderecoCompleto);
    
    if (coordenadas && coordenadas.lat && coordenadas.lng && coordenadas.lat !== 0 && coordenadas.lng !== 0) {
      console.log(`✅ Coordenadas obtidas para ${prefixo}:`, coordenadas);
      
      // Tentar usar setValues primeiro (para Formik)
      if (setValues) {
        setValues((prevValues) => ({
          ...prevValues,
          [`${prefixo}_lat`]: coordenadas.lat,
          [`${prefixo}_lng`]: coordenadas.lng,
        }));
      }
      
      // Também usar setFieldValue se disponível (para garantir que funcione)
      if (setFieldValue) {
        setFieldValue(`${prefixo}_lat`, coordenadas.lat);
        setFieldValue(`${prefixo}_lng`, coordenadas.lng);
      }
    } else {
      console.warn(`⚠️ Não foi possível obter coordenadas para ${prefixo}`);
    }
  } catch (error) {
    console.error(`❌ Erro ao geocodificar endereço do ${prefixo}:`, error);
  }
};

// Função para buscar código IBGE por município e UF
const buscarCodigoIbge = async (municipio, uf, setFieldValue, prefixo) => {
  if (!municipio || !uf) {
    return;
  }

  console.log(`🔍 Buscando código IBGE para ${municipio} - ${uf} (${prefixo})`);

  try {
    // Tentar buscar pelo CEP primeiro (se houver CEP preenchido)
    // Se não houver CEP, usar uma busca por município e UF
    // A API Nuvem Fiscal pode ter um endpoint para buscar por município/UF
    // Por enquanto, vamos tentar buscar usando a API de CEP com um CEP genérico do município
    
    // Alternativa: buscar código IBGE usando API do IBGE diretamente
    // Por enquanto, vamos usar a API Nuvem Fiscal se disponível
    // ou fazer uma busca manual
    
    // Se já temos o código IBGE, não precisamos buscar novamente
    // Esta função será chamada quando UF e Município forem preenchidos
    // e o código IBGE estiver vazio
    
    console.log(`✅ Busca de código IBGE iniciada para ${municipio} - ${uf}`);
  } catch (error) {
    console.error('❌ Erro ao buscar código IBGE:', error);
  }
};


// Lista de UFs do Brasil
const UFS_BRASIL = [
  { value: 'AC', label: 'AC - Acre' },
  { value: 'AL', label: 'AL - Alagoas' },
  { value: 'AP', label: 'AP - Amapá' },
  { value: 'AM', label: 'AM - Amazonas' },
  { value: 'BA', label: 'BA - Bahia' },
  { value: 'CE', label: 'CE - Ceará' },
  { value: 'DF', label: 'DF - Distrito Federal' },
  { value: 'ES', label: 'ES - Espírito Santo' },
  { value: 'GO', label: 'GO - Goiás' },
  { value: 'MA', label: 'MA - Maranhão' },
  { value: 'MT', label: 'MT - Mato Grosso' },
  { value: 'MS', label: 'MS - Mato Grosso do Sul' },
  { value: 'MG', label: 'MG - Minas Gerais' },
  { value: 'PA', label: 'PA - Pará' },
  { value: 'PB', label: 'PB - Paraíba' },
  { value: 'PR', label: 'PR - Paraná' },
  { value: 'PE', label: 'PE - Pernambuco' },
  { value: 'PI', label: 'PI - Piauí' },
  { value: 'RJ', label: 'RJ - Rio de Janeiro' },
  { value: 'RN', label: 'RN - Rio Grande do Norte' },
  { value: 'RS', label: 'RS - Rio Grande do Sul' },
  { value: 'RO', label: 'RO - Rondônia' },
  { value: 'RR', label: 'RR - Roraima' },
  { value: 'SC', label: 'SC - Santa Catarina' },
  { value: 'SP', label: 'SP - São Paulo' },
  { value: 'SE', label: 'SE - Sergipe' },
  { value: 'TO', label: 'TO - Tocantins' },
];

// Componente reutilizável para o formulário de viagem
function ViagemForm({ values, handleChange, setFieldValue, setValues, errors, touched, activeTab, truckers, providers = [], embarcadores = [] }) {
  const dropdownEmbarcadorRef = useRef(null);
  const [embarcadorInput, setEmbarcadorInput] = useState('');
  const [showDropdownEmbarcador, setShowDropdownEmbarcador] = useState(false);

  const embarcadoresFiltrados = useMemo(() => {
    const term = String(embarcadorInput || '').toLowerCase().trim();
    if (!term) return embarcadores || [];

    const termDigits = term.replace(/\D/g, '');

    return (embarcadores || []).filter((e) => {
      const nome = String(e?.nome || '').toLowerCase();
      const fantasia = String(e?.nomeFantasia || '').toLowerCase();
      const cpfCnpj = String(e?.cpfCnpj || '').replace(/\D/g, '');

      return (
        nome.includes(term) ||
        fantasia.includes(term) ||
        (termDigits && cpfCnpj.includes(termDigits))
      );
    });
  }, [embarcadorInput, embarcadores]);

  useEffect(() => {
    const id = values?.embarcador_id;
    if (!id) {
      if (!embarcadorInput) return;
      // Se limpar o ID, limpar o texto também (somente se o usuário não está digitando)
      return;
    }

    const selected = (embarcadores || []).find((e) => String(e.id) === String(id));
    if (selected) {
      const label = `${selected.nome || selected.nomeFantasia || 'Sem nome'}${selected.cpfCnpj ? ` - ${selected.cpfCnpj}` : ''}`;
      if (embarcadorInput !== label) setEmbarcadorInput(label);
    }
  }, [values?.embarcador_id, embarcadores, embarcadorInput]);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownEmbarcadorRef.current && !dropdownEmbarcadorRef.current.contains(event.target)) {
        setShowDropdownEmbarcador(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);
  
  // Função auxiliar para preencher dados do tomador baseado no tipo
  const preencherTomadorPorTipo = (tipoTomador, currentValues) => {
    if (tipoTomador === '0') {
      // Remetente - copiar dados do remetente
      return {
        tomador_cpf_cnpj: currentValues.remetente_cpf_cnpj || '',
        tomador_nome_razao_social: currentValues.remetente_nome_razao_social || '',
        tomador_inscricao_estadual: currentValues.remetente_inscricao_estadual || '',
        tomador_cep: currentValues.remetente_cep || '',
        tomador_logradouro: currentValues.remetente_logradouro || '',
        tomador_numero: currentValues.remetente_numero || '',
        tomador_complemento: currentValues.remetente_complemento || '',
        tomador_bairro: currentValues.remetente_bairro || '',
        tomador_uf: currentValues.remetente_uf || '',
        tomador_municipio_codigo: currentValues.remetente_municipio_codigo || '',
        tomador_municipio_descricao: currentValues.remetente_municipio_descricao || '',
        tomador_telefone_ddd: currentValues.remetente_telefone_ddd || '',
        tomador_telefone_numero: currentValues.remetente_telefone_numero || '',
        tomador_email: currentValues.remetente_email || '',
      };
    } else if (tipoTomador === '3') {
      // Destinatário - copiar dados do destinatário
      return {
        tomador_cpf_cnpj: currentValues.destinatario_cpf_cnpj || '',
        tomador_nome_razao_social: currentValues.destinatario_nome_razao_social || '',
        tomador_inscricao_estadual: currentValues.destinatario_inscricao_estadual || '',
        tomador_cep: currentValues.destinatario_cep || '',
        tomador_logradouro: currentValues.destinatario_logradouro || '',
        tomador_numero: currentValues.destinatario_numero || '',
        tomador_complemento: currentValues.destinatario_complemento || '',
        tomador_bairro: currentValues.destinatario_bairro || '',
        tomador_uf: currentValues.destinatario_uf || '',
        tomador_municipio_codigo: currentValues.destinatario_municipio_codigo || '',
        tomador_municipio_descricao: currentValues.destinatario_municipio_descricao || '',
        tomador_telefone_ddd: currentValues.destinatario_telefone_ddd || '',
        tomador_telefone_numero: currentValues.destinatario_telefone_numero || '',
        tomador_email: currentValues.destinatario_email || '',
      };
    }
    return {};
  };

  // useEffect para preencher automaticamente quando o tipo for Remetente/Destinatário e os campos estiverem vazios
  useEffect(() => {
    const tipoTomador = values.tomador_tipo || '0';
    
    // Verificar se os campos do tomador estão vazios (apenas CPF/CNPJ e nome como indicador)
    const camposVazios = !values.tomador_cpf_cnpj && !values.tomador_nome_razao_social;
    
    // Se o tipo for Remetente (0) e os campos estiverem vazios
    if (tipoTomador === '0' && camposVazios) {
      // Verificar se há dados do remetente para preencher
      const temDadosRemetente = !!(values.remetente_cpf_cnpj || values.remetente_nome_razao_social);
      if (temDadosRemetente) {
        const dadosParaPreencher = preencherTomadorPorTipo('0', values);
        // Verificar se realmente há dados para preencher
        if (dadosParaPreencher.tomador_cpf_cnpj || dadosParaPreencher.tomador_nome_razao_social) {
          setValues((prevValues) => {
            // Verificar novamente se ainda está vazio (evitar sobrescrever dados já preenchidos)
            if (!prevValues.tomador_cpf_cnpj && !prevValues.tomador_nome_razao_social) {
              return {
                ...prevValues,
                ...dadosParaPreencher,
              };
            }
            return prevValues;
          });
        }
      }
    }
    // Se o tipo for Destinatário (3) e os campos estiverem vazios
    else if (tipoTomador === '3' && camposVazios) {
      // Verificar se há dados do destinatário para preencher
      const temDadosDestinatario = !!(values.destinatario_cpf_cnpj || values.destinatario_nome_razao_social);
      if (temDadosDestinatario) {
        const dadosParaPreencher = preencherTomadorPorTipo('3', values);
        // Verificar se realmente há dados para preencher
        if (dadosParaPreencher.tomador_cpf_cnpj || dadosParaPreencher.tomador_nome_razao_social) {
          setValues((prevValues) => {
            // Verificar novamente se ainda está vazio (evitar sobrescrever dados já preenchidos)
            if (!prevValues.tomador_cpf_cnpj && !prevValues.tomador_nome_razao_social) {
              return {
                ...prevValues,
                ...dadosParaPreencher,
              };
            }
            return prevValues;
          });
        }
      }
    }
  }, [
    values.tomador_tipo,
    values.remetente_cpf_cnpj,
    values.remetente_nome_razao_social,
    values.destinatario_cpf_cnpj,
    values.destinatario_nome_razao_social,
    setValues,
  ]);

  // Buscar código IBGE quando UF e Município forem preenchidos no remetente
  useEffect(() => {
    const uf = values.remetente_uf;
    const municipio = values.remetente_municipio_descricao;
    const codigoIbge = values.remetente_municipio_codigo;
    
    // Se UF e Município estão preenchidos mas código IBGE está vazio
    if (uf && municipio && !codigoIbge) {
      // Tentar buscar código IBGE usando a API de CEP
      // Se houver CEP, usar buscarCep, senão tentar buscar por município/UF
      const cep = values.remetente_cep;
      if (cep && cep.replace(/\D/g, '').length === 8) {
        // Se temos CEP, buscar usando buscarCep
        buscarCep(cep, setFieldValue, setValues, values, 'remetente');
      } else {
        // Se não temos CEP, tentar buscar código IBGE por município e UF
        // Por enquanto, vamos deixar o usuário preencher manualmente ou usar o CEP
        console.log(`ℹ️ Para preencher automaticamente o código IBGE, informe o CEP ou preencha manualmente`);
      }
    }
  }, [values.remetente_uf, values.remetente_municipio_descricao, values.remetente_cep, values.remetente_municipio_codigo, setFieldValue, setValues, values]);

  // Buscar código IBGE quando UF e Município forem preenchidos no destinatário
  useEffect(() => {
    const uf = values.destinatario_uf;
    const municipio = values.destinatario_municipio_descricao;
    const codigoIbge = values.destinatario_municipio_codigo;
    
    // Se UF e Município estão preenchidos mas código IBGE está vazio
    if (uf && municipio && !codigoIbge) {
      // Tentar buscar código IBGE usando a API de CEP
      // Se houver CEP, usar buscarCep, senão tentar buscar por município/UF
      const cep = values.destinatario_cep;
      if (cep && cep.replace(/\D/g, '').length === 8) {
        // Se temos CEP, buscar usando buscarCep
        buscarCep(cep, setFieldValue, setValues, values, 'destinatario');
      } else {
        // Se não temos CEP, tentar buscar código IBGE por município e UF
        // Por enquanto, vamos deixar o usuário preencher manualmente ou usar o CEP
        console.log(`ℹ️ Para preencher automaticamente o código IBGE do destinatário, informe o CEP ou preencha manualmente`);
      }
    }
  }, [values.destinatario_uf, values.destinatario_municipio_descricao, values.destinatario_cep, values.destinatario_municipio_codigo, setFieldValue, setValues, values]);

  // Geocodificar endereço do remetente quando os campos estiverem preenchidos
  useEffect(() => {
    const logradouro = values.remetente_logradouro;
    const municipio = values.remetente_municipio_descricao;
    const uf = values.remetente_uf;
    
    console.log('🔵 [Remetente] Verificando campos:', { logradouro, municipio, uf });
    
    // Só geocodificar se tiver endereço completo (logradouro, município e UF)
    if (logradouro && municipio && uf) {
      console.log('🔵 [Remetente] Campos completos, agendando geocodificação...');
      // Usar debounce de 1.5 segundos para evitar muitas chamadas durante a digitação
      const timeoutId = setTimeout(() => {
        console.log('🔵 [Remetente] Executando geocodificação...');
        // Sempre geocodificar quando o endereço mudar, mesmo se já tiver coordenadas
        // Isso garante que as coordenadas sejam atualizadas se o endereço for alterado
        geocodificarEndereco('remetente', setFieldValue, setValues, values);
      }, 1500);
      
      return () => clearTimeout(timeoutId);
    } else {
      console.log('🔵 [Remetente] Campos incompletos, aguardando preenchimento...');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [values.remetente_logradouro, values.remetente_numero, values.remetente_bairro, values.remetente_municipio_descricao, values.remetente_uf, values.remetente_cep]);

  // Geocodificar endereço do destinatário quando os campos estiverem preenchidos
  useEffect(() => {
    const logradouro = values.destinatario_logradouro;
    const municipio = values.destinatario_municipio_descricao;
    const uf = values.destinatario_uf;
    
    console.log('🔵 [Destinatário] Verificando campos:', { logradouro, municipio, uf });
    
    // Só geocodificar se tiver endereço completo (logradouro, município e UF)
    if (logradouro && municipio && uf) {
      console.log('🔵 [Destinatário] Campos completos, agendando geocodificação...');
      // Usar debounce de 1.5 segundos para evitar muitas chamadas durante a digitação
      const timeoutId = setTimeout(() => {
        console.log('🔵 [Destinatário] Executando geocodificação...');
        // Sempre geocodificar quando o endereço mudar, mesmo se já tiver coordenadas
        // Isso garante que as coordenadas sejam atualizadas se o endereço for alterado
        geocodificarEndereco('destinatario', setFieldValue, setValues, values);
      }, 1500);
      
      return () => clearTimeout(timeoutId);
    } else {
      console.log('🔵 [Destinatário] Campos incompletos, aguardando preenchimento...');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [values.destinatario_logradouro, values.destinatario_numero, values.destinatario_bairro, values.destinatario_municipio_descricao, values.destinatario_uf, values.destinatario_cep]);

  // Buscar código IBGE quando UF e Município forem preenchidos no tomador
  useEffect(() => {
    const uf = values.tomador_uf;
    const municipio = values.tomador_municipio_descricao;
    const codigoIbge = values.tomador_municipio_codigo;
    
    // Se UF e Município estão preenchidos mas código IBGE está vazio
    if (uf && municipio && !codigoIbge) {
      // Tentar buscar código IBGE usando a API de CEP
      // Se houver CEP, usar buscarCep, senão tentar buscar por município/UF
      const cep = values.tomador_cep;
      if (cep && cep.replace(/\D/g, '').length === 8) {
        // Se temos CEP, buscar usando buscarCep
        buscarCep(cep, setFieldValue, setValues, values, 'tomador');
      } else {
        // Se não temos CEP, tentar buscar código IBGE por município e UF
        // Por enquanto, vamos deixar o usuário preencher manualmente ou usar o CEP
        console.log(`ℹ️ Para preencher automaticamente o código IBGE do tomador, informe o CEP ou preencha manualmente`);
      }
    }
  }, [values.tomador_uf, values.tomador_municipio_descricao, values.tomador_cep, values.tomador_municipio_codigo, setFieldValue, setValues, values]);

  return (
    <>
      {/* Tab Geral */}
      {activeTab === 0 && (
        <>
          <SectionTitle>Informações Gerais</SectionTitle>
          <Row>
            <Column>
              <WrapperInput>
                <TextField
                  label="Ambiente"
                  name="ambiente"
                  value={values.ambiente}
                  onChange={handleChange}
                  select
                  disabled
                  helperText="Ambiente definido nos Parâmetros SEFAZ"
                  fullWidth
                  color="secondary"
                >
                  <option value="homologacao">Homologação</option>
                  <option value="producao">Produção</option>
                </TextField>
              </WrapperInput>
            </Column>
            <Column>
              <WrapperInput>
                <TextField
                  label="Referência"
                  name="referencia"
                  value={values.referencia}
                  onChange={handleChange}
                  fullWidth
                  color="secondary"
                />
              </WrapperInput>
            </Column>
          </Row>
          <Row>
            <Column>
              <WrapperInput>
                <TextField
                  label="Data de Emissão"
                  name="data_emissao"
                  type="date"
                  value={values.data_emissao}
                  onChange={handleChange}
                  fullWidth
                  color="secondary"
                  InputLabelProps={{ shrink: true }}
                />
              </WrapperInput>
            </Column>
            <Column>
              <WrapperInput>
                <TextField
                  label="Tipo CT-e"
                  name="tipo_cte"
                  value={values.tipo_cte}
                  onChange={handleChange}
                  select
                  fullWidth
                  color="secondary"
                >
                  <option value="0">Normal</option>
                  <option value="1">Complemento de Valores</option>
                  <option value="2">Anulação</option>
                  <option value="3">Substituição</option>
                </TextField>
              </WrapperInput>
            </Column>
          </Row>
          <Row>
            <Column>
              <WrapperInput>
                <TextField
                  label="Natureza da Operação"
                  name="natureza_operacao"
                  value={values.natureza_operacao}
                  onChange={handleChange}
                  select
                  fullWidth
                  color="secondary"
                >
                  <option value="">Selecione</option>
                  <option value="Transporte de carga">Transporte de carga</option>
                  <option value="Transporte">Transporte</option>
                  <option value="Prestação de serviço de transporte">Prestação de serviço de transporte</option>
                  <option value="Transporte rodoviário de carga">Transporte rodoviário de carga</option>
                  <option value="Transporte aéreo de carga">Transporte aéreo de carga</option>
                  <option value="Transporte aquaviário de carga">Transporte aquaviário de carga</option>
                  <option value="Transporte ferroviário de carga">Transporte ferroviário de carga</option>
                </TextField>
              </WrapperInput>
            </Column>
            <Column>
              <WrapperInput>
                <TextField
                  label="CFOP"
                  name="cfop"
                  value={values.cfop}
                  onChange={handleChange}
                  select
                  fullWidth
                  color="secondary"
                >
                  <option value="">Selecione</option>
                  <option value="5353">5353 - Prestação de serviço de transporte rodoviário de carga</option>
                  <option value="5356">5356 - Prestação de serviço de transporte rodoviário de passageiros</option>
                  <option value="5933">5933 - Prestação de serviço de transporte aéreo de carga</option>
                  <option value="5932">5932 - Prestação de serviço de transporte aéreo de passageiros</option>
                  <option value="6353">6353 - Prestação de serviço de transporte aquaviário de carga</option>
                  <option value="6356">6356 - Prestação de serviço de transporte aquaviário de passageiros</option>
                  <option value="7353">7353 - Prestação de serviço de transporte ferroviário de carga</option>
                  <option value="7356">7356 - Prestação de serviço de transporte ferroviário de passageiros</option>
                </TextField>
              </WrapperInput>
            </Column>
          </Row>
          <Row>
            <Column>
              <WrapperInput>
                <TextField
                  label="Tipo de Serviço"
                  name="tipo_servico"
                  value={values.tipo_servico}
                  onChange={handleChange}
                  select
                  fullWidth
                  color="secondary"
                >
                  <option value="0">Normal</option>
                  <option value="1">Subcontratação</option>
                  <option value="2">Redespacho</option>
                  <option value="3">Redespacho Intermediário</option>
                  <option value="4">Serviço Vinculado a Multimodal</option>
                </TextField>
              </WrapperInput>
            </Column>
            <Column>
              <WrapperInput>
                <TextField
                  label="Modal"
                  name="modal"
                  value={values.modal}
                  onChange={handleChange}
                  select
                  fullWidth
                  color="secondary"
                >
                  <option value="01">Rodoviário</option>
                  <option value="02">Aéreo</option>
                  <option value="03">Aquaviário</option>
                  <option value="04">Ferroviário</option>
                  <option value="05">Dutoviário</option>
                  <option value="06">Multimodal</option>
                </TextField>
              </WrapperInput>
            </Column>
          </Row>
        </>
      )}

      {/* Tab Emitente */}
      {activeTab === 1 && (
        <>
          <SectionTitle>Dados do Emitente (Transportadora)</SectionTitle>
          <Row>
            <Column>
              <WrapperInput>
                <TextField
                  label="Selecionar Empresa Cadastrada"
                  name="emitente_empresa_id"
                  value={values.emitente_empresa_id || ''}
                  onChange={(e) => {
                    const empresaId = e.target.value;
                    setFieldValue('emitente_empresa_id', empresaId);
                    
                    if (empresaId) {
                      const empresa = providers.find(p => p.id === empresaId);
                      if (empresa) {
                        // Extrair telefone - pode estar em campos separados (phone_ddd, phone_number) ou combinado (phone)
                        let telefoneDdd = empresa.phone_ddd || '';
                        let telefoneNumero = empresa.phone_number || '';
                        
                        // Se não estiver separado, tentar extrair do campo phone
                        if (!telefoneDdd && !telefoneNumero && empresa.phone) {
                          const phoneClean = empresa.phone.replace(/\D/g, '');
                          telefoneDdd = phoneClean.length >= 10 ? phoneClean.substring(0, 2) : (phoneClean.length >= 2 ? phoneClean.substring(0, 2) : '');
                          telefoneNumero = phoneClean.length >= 10 ? phoneClean.substring(2) : (phoneClean.length > 2 ? phoneClean.substring(2) : phoneClean);
                        }
                        
                        // Preencher dados do emitente com os dados da empresa
                        setValues((prevValues) => ({
                          ...prevValues,
                          emitente_cpf_cnpj: empresa.cnpj || empresa.cpf || empresa.cpf_cnpj || '',
                          emitente_nome_razao_social: empresa.name || empresa.nome || empresa.social_reason || empresa.razao_social || empresa.nome_razao_social || '',
                          emitente_nome_fantasia: empresa.fantasy_name || empresa.nome_fantasia || '',
                          emitente_inscricao_estadual: empresa.state_subscribe || empresa.inscricao_estadual || empresa.ie || '',
                          emitente_inscricao_municipal: empresa.municipal_subscribe || empresa.inscricao_municipal || empresa.im || '',
                          emitente_cep: empresa.zipcode || empresa.cep || empresa.address?.cep || empresa.endereco?.cep || '',
                          emitente_logradouro: empresa.address || empresa.address?.street || empresa.endereco?.logradouro || empresa.logradouro || '',
                          emitente_numero: empresa.number || empresa.address?.number || empresa.endereco?.numero || empresa.numero || '',
                          emitente_complemento: empresa.complement || empresa.address?.complement || empresa.endereco?.complemento || empresa.complemento || '',
                          emitente_bairro: empresa.neighborhood || empresa.address?.neighborhood || empresa.endereco?.bairro || empresa.bairro || '',
                          emitente_uf: empresa.state || empresa.address?.state || empresa.endereco?.uf || empresa.uf || '',
                          emitente_municipio_codigo: empresa.codigo_ibge || empresa.codigo_ibge_municipio || empresa.city_code || empresa.municipio_codigo || empresa.address?.city_code || empresa.endereco?.municipio?.codigo_ibge || empresa.address?.municipio?.codigo_ibge || '',
                          emitente_municipio_descricao: empresa.city || empresa.address?.city || empresa.endereco?.municipio?.descricao || empresa.municipio || empresa.municipio_descricao || '',
                          emitente_telefone_ddd: telefoneDdd,
                          emitente_telefone_numero: telefoneNumero,
                          emitente_email: empresa.email || '',
                          emitente_rntrc: empresa.rntrc || '',
                        }));
                      }
                    }
                  }}
                  select
                  fullWidth
                  color="secondary"
                >
                  <option value="">Selecione uma empresa</option>
                  {providers.map((provider) => {
                    const nome = provider.name || provider.nome || provider.social_reason || provider.razao_social || provider.nome_razao_social || 'Sem nome';
                    const cnpj = provider.cnpj || provider.cpf || provider.cpf_cnpj || 'Sem CNPJ';
                    return (
                      <option key={provider.id} value={provider.id}>
                        {nome} - {cnpj}
                      </option>
                    );
                  })}
                </TextField>
              </WrapperInput>
            </Column>
          </Row>
          <Row>
            <Column>
              <WrapperInput>
                <CpfCnpjMaskedInput
                  value={values.emitente_cpf_cnpj}
                  onChange={(e) => setFieldValue('emitente_cpf_cnpj', e.target.value)}
                >
                  {(inputProps) => (
                    <TextField
                      {...inputProps}
                      label="CPF/CNPJ"
                      fullWidth
                      color="secondary"
                      inputProps={{ maxLength: 18 }} // CPF: 14 chars, CNPJ: 18 chars
                    />
                  )}
                </CpfCnpjMaskedInput>
              </WrapperInput>
            </Column>
            <Column>
              <WrapperInput>
                <TextField
                  label="Razão Social"
                  name="emitente_nome_razao_social"
                  value={values.emitente_nome_razao_social}
                  onChange={handleChange}
                  fullWidth
                  color="secondary"
                />
              </WrapperInput>
            </Column>
          </Row>
          <Row>
            <Column>
              <WrapperInput>
                <TextField
                  label="Nome Fantasia"
                  name="emitente_nome_fantasia"
                  value={values.emitente_nome_fantasia}
                  onChange={handleChange}
                  fullWidth
                  color="secondary"
                />
              </WrapperInput>
            </Column>
            <Column>
              <WrapperInput>
                <TextField
                  label="Inscrição Estadual"
                  name="emitente_inscricao_estadual"
                  value={values.emitente_inscricao_estadual}
                  onChange={handleChange}
                  fullWidth
                  color="secondary"
                />
              </WrapperInput>
            </Column>
          </Row>
          <Row>
            <Column>
              <WrapperInput>
                <TextField
                  label="Inscrição Municipal"
                  name="emitente_inscricao_municipal"
                  value={values.emitente_inscricao_municipal}
                  onChange={handleChange}
                  fullWidth
                  color="secondary"
                />
              </WrapperInput>
            </Column>
            <Column>
              <WrapperInput>
                <MaskedInput
                  mask="99999-999"
                  value={values.emitente_cep}
                  onChange={(e) => {
                    const cepValue = e.target.value;
                    console.log('CEP onChange:', cepValue);
                    setFieldValue('emitente_cep', cepValue);
                    
                    // Buscar CEP automaticamente quando estiver completo (8 dígitos)
                    const cepLimpo = cepValue.replace(/\D/g, '');
                    if (cepLimpo.length === 8) {
                      console.log('🔵 CEP completo no onChange, chamando buscarCep...');
                      // Usar setTimeout para garantir que o setFieldValue foi processado
                      setTimeout(() => {
                        buscarCep(cepValue, setFieldValue, setValues, values, 'emitente');
                      }, 100);
                    }
                  }}
                  onBlur={(e) => {
                    const cep = e.target.value;
                    console.log('🔵 MaskedInput onBlur chamado para CEP emitente:', cep);
                    const cepLimpo = cep.replace(/\D/g, '');
                    console.log('🔵 CEP limpo:', cepLimpo, 'tamanho:', cepLimpo.length);
                    
                    // Busca automaticamente quando o CEP estiver completo (8 dígitos)
                    if (cepLimpo.length === 8) {
                      console.log('🔵 CEP completo no onBlur, chamando buscarCep...');
                      buscarCep(cep, setFieldValue, setValues, values, 'emitente');
                    } else {
                      console.log('🔵 CEP incompleto, não buscando');
                    }
                  }}
                >
                  {(inputProps) => {
                    // Remover onBlur dos inputProps pois não pode ser passado para o filho
                    const { onBlur, ...restProps } = inputProps;
                    
                    return (
                      <TextField
                        {...restProps}
                        label="CEP"
                        fullWidth
                        color="secondary"
                      />
                    );
                  }}
                </MaskedInput>
              </WrapperInput>
            </Column>
            <Column>
              <WrapperInput>
                <TextField
                  label="Logradouro"
                  name="emitente_logradouro"
                  value={values.emitente_logradouro}
                  onChange={handleChange}
                  fullWidth
                  color="secondary"
                />
              </WrapperInput>
            </Column>
          </Row>
          <Row>
            <Column>
              <WrapperInput>
                <TextField
                  label="Número"
                  name="emitente_numero"
                  value={values.emitente_numero}
                  onChange={handleChange}
                  fullWidth
                  color="secondary"
                />
              </WrapperInput>
            </Column>
            <Column>
              <WrapperInput>
                <TextField
                  label="Complemento"
                  name="emitente_complemento"
                  value={values.emitente_complemento}
                  onChange={handleChange}
                  fullWidth
                  color="secondary"
                />
              </WrapperInput>
            </Column>
          </Row>
          <Row>
            <Column>
              <WrapperInput>
                <TextField
                  label="Bairro"
                  name="emitente_bairro"
                  value={values.emitente_bairro}
                  onChange={handleChange}
                  fullWidth
                  color="secondary"
                />
              </WrapperInput>
            </Column>
            <Column>
              <WrapperInput>
                <TextField
                  label="UF"
                  name="emitente_uf"
                  value={values.emitente_uf}
                  onChange={handleChange}
                  select
                  fullWidth
                  color="secondary"
                >
                  <option value="">Selecione</option>
                  {UFS_BRASIL.map((uf) => (
                    <option key={uf.value} value={uf.value}>
                      {uf.label}
                    </option>
                  ))}
                </TextField>
              </WrapperInput>
            </Column>
          </Row>
          <Row>
            <Column>
              <WrapperInput>
                <TextField
                  label="Código IBGE Município"
                  name="emitente_municipio_codigo"
                  value={values.emitente_municipio_codigo}
                  onChange={handleChange}
                  fullWidth
                  color="secondary"
                />
              </WrapperInput>
            </Column>
            <Column>
              <WrapperInput>
                <TextField
                  label="Município"
                  name="emitente_municipio_descricao"
                  value={values.emitente_municipio_descricao}
                  onChange={handleChange}
                  fullWidth
                  color="secondary"
                />
              </WrapperInput>
            </Column>
          </Row>
          <Row>
            <Column>
              <WrapperInput>
                <TextField
                  label="DDD Telefone"
                  name="emitente_telefone_ddd"
                  value={values.emitente_telefone_ddd}
                  onChange={handleChange}
                  fullWidth
                  color="secondary"
                />
              </WrapperInput>
            </Column>
            <Column>
              <WrapperInput>
                <TextField
                  label="Telefone"
                  name="emitente_telefone_numero"
                  value={values.emitente_telefone_numero}
                  onChange={handleChange}
                  fullWidth
                  color="secondary"
                />
              </WrapperInput>
            </Column>
          </Row>
          <WrapperInput>
            <TextField
              label="E-mail"
              name="emitente_email"
              type="email"
              value={values.emitente_email}
              onChange={handleChange}
              fullWidth
              color="secondary"
            />
          </WrapperInput>
          <Row>
            <Column>
              <WrapperInput>
                <TextField
                  label="RNTRC *"
                  name="emitente_rntrc"
                  value={values.emitente_rntrc}
                  onChange={handleChange}
                  fullWidth
                  color="secondary"
                  inputProps={{ maxLength: 8 }}
                  helperText="Registro Nacional de Transportador de Carga (8 dígitos)"
                />
              </WrapperInput>
            </Column>
          </Row>
        </>
      )}

      {/* Tab Remetente */}
      {activeTab === 2 && (
        <>
          <SectionTitle>Dados do Remetente</SectionTitle>
          <Row>
            <Column>
              <WrapperInput>
                <CpfCnpjMaskedInput
                  value={values.remetente_cpf_cnpj}
                  onChange={(e) => setFieldValue('remetente_cpf_cnpj', e.target.value)}
                >
                  {(inputProps) => (
                    <TextField
                      {...inputProps}
                      label="CPF/CNPJ"
                      fullWidth
                      color="secondary"
                      inputProps={{ maxLength: 18 }} // CPF: 14 chars, CNPJ: 18 chars
                    />
                  )}
                </CpfCnpjMaskedInput>
              </WrapperInput>
            </Column>
            <Column>
              <WrapperInput>
                <TextField
                  label="Razão Social"
                  name="remetente_nome_razao_social"
                  value={values.remetente_nome_razao_social}
                  onChange={handleChange}
                  fullWidth
                  color="secondary"
                />
              </WrapperInput>
            </Column>
          </Row>
          <Row>
            <Column>
              <WrapperInput>
                <TextField
                  label="Inscrição Estadual"
                  name="remetente_inscricao_estadual"
                  value={values.remetente_inscricao_estadual}
                  onChange={handleChange}
                  fullWidth
                  color="secondary"
                />
              </WrapperInput>
            </Column>
            <Column>
              <WrapperInput>
                <MaskedInput
                  mask="99999-999"
                  value={values.remetente_cep}
                  onChange={(e) => {
                    const cepValue = e.target.value;
                    setFieldValue('remetente_cep', cepValue);
                    
                    // Buscar CEP automaticamente quando estiver completo (8 dígitos)
                    const cepLimpo = cepValue.replace(/\D/g, '');
                    if (cepLimpo.length === 8) {
                      console.log('🔵 CEP completo no onChange (remetente), chamando buscarCep...');
                      setTimeout(() => {
                        buscarCep(cepValue, setFieldValue, setValues, values, 'remetente');
                      }, 100);
                    }
                  }}
                  onBlur={(e) => {
                    const cep = e.target.value;
                    console.log('🔵 MaskedInput onBlur chamado para CEP remetente:', cep);
                    const cepLimpo = cep.replace(/\D/g, '');
                    // Busca automaticamente quando o CEP estiver completo (8 dígitos)
                    if (cepLimpo.length === 8) {
                      console.log('🔵 CEP completo no onBlur (remetente), chamando buscarCep...');
                      buscarCep(cep, setFieldValue, setValues, values, 'remetente');
                    }
                  }}
                >
                  {(inputProps) => {
                    // Remover onBlur dos inputProps pois não pode ser passado para o filho
                    const { onBlur, ...restProps } = inputProps;
                    return (
                      <TextField
                        {...restProps}
                        label="CEP"
                        fullWidth
                        color="secondary"
                      />
                    );
                  }}
                </MaskedInput>
              </WrapperInput>
            </Column>
          </Row>
          <Row>
            <Column>
              <WrapperInput>
                <TextField
                  label="Logradouro"
                  name="remetente_logradouro"
                  value={values.remetente_logradouro}
                  onChange={handleChange}
                  fullWidth
                  color="secondary"
                />
              </WrapperInput>
            </Column>
            <Column>
              <WrapperInput>
                <TextField
                  label="Número"
                  name="remetente_numero"
                  value={values.remetente_numero}
                  onChange={handleChange}
                  fullWidth
                  color="secondary"
                />
              </WrapperInput>
            </Column>
          </Row>
          <Row>
            <Column>
              <WrapperInput>
                <TextField
                  label="Complemento"
                  name="remetente_complemento"
                  value={values.remetente_complemento}
                  onChange={handleChange}
                  fullWidth
                  color="secondary"
                />
              </WrapperInput>
            </Column>
            <Column>
              <WrapperInput>
                <TextField
                  label="Bairro"
                  name="remetente_bairro"
                  value={values.remetente_bairro}
                  onChange={handleChange}
                  fullWidth
                  color="secondary"
                />
              </WrapperInput>
            </Column>
          </Row>
          <Row>
            <Column>
              <WrapperInput>
                <TextField
                  label="UF"
                  name="remetente_uf"
                  value={values.remetente_uf}
                  onChange={handleChange}
                  select
                  fullWidth
                  color="secondary"
                >
                  <option value="">Selecione</option>
                  {UFS_BRASIL.map((uf) => (
                    <option key={uf.value} value={uf.value}>
                      {uf.label}
                    </option>
                  ))}
                </TextField>
              </WrapperInput>
            </Column>
            <Column>
              <WrapperInput>
                <TextField
                  label="Código IBGE Município"
                  name="remetente_municipio_codigo"
                  value={values.remetente_municipio_codigo}
                  onChange={handleChange}
                  fullWidth
                  color="secondary"
                />
              </WrapperInput>
            </Column>
          </Row>
          <Row>
            <Column>
              <WrapperInput>
                <TextField
                  label="Município"
                  name="remetente_municipio_descricao"
                  value={values.remetente_municipio_descricao}
                  onChange={handleChange}
                  fullWidth
                  color="secondary"
                />
              </WrapperInput>
            </Column>
            <Column>
              <WrapperInput>
                <TextField
                  label="DDD Telefone"
                  name="remetente_telefone_ddd"
                  value={values.remetente_telefone_ddd}
                  onChange={handleChange}
                  fullWidth
                  color="secondary"
                />
              </WrapperInput>
            </Column>
          </Row>
          <Row>
            <Column>
              <WrapperInput>
                <TextField
                  label="Telefone"
                  name="remetente_telefone_numero"
                  value={values.remetente_telefone_numero}
                  onChange={handleChange}
                  fullWidth
                  color="secondary"
                />
              </WrapperInput>
            </Column>
            <Column>
              <WrapperInput>
                <TextField
                  label="E-mail"
                  name="remetente_email"
                  type="email"
                  value={values.remetente_email}
                  onChange={handleChange}
                  fullWidth
                  color="secondary"
                />
              </WrapperInput>
            </Column>
          </Row>
          <Row>
            <Column>
              <WrapperInput>
                <TextField
                  label="Latitude"
                  name="remetente_lat"
                  value={values.remetente_lat || ''}
                  onChange={handleChange}
                  fullWidth
                  color="secondary"
                  disabled
                  helperText="Preenchido automaticamente ao buscar o endereço"
                />
              </WrapperInput>
            </Column>
            <Column>
              <WrapperInput>
                <TextField
                  label="Longitude"
                  name="remetente_lng"
                  value={values.remetente_lng || ''}
                  onChange={handleChange}
                  fullWidth
                  color="secondary"
                  disabled
                  helperText="Preenchido automaticamente ao buscar o endereço"
                />
              </WrapperInput>
            </Column>
          </Row>
        </>
      )}

      {/* Tab Destinatário */}
      {activeTab === 3 && (
        <>
          <SectionTitle>Dados do Destinatário</SectionTitle>
          <Row>
            <Column>
              <WrapperInput>
                <CpfCnpjMaskedInput
                  value={values.destinatario_cpf_cnpj}
                  onChange={(e) => setFieldValue('destinatario_cpf_cnpj', e.target.value)}
                >
                  {(inputProps) => (
                    <TextField
                      {...inputProps}
                      label="CPF/CNPJ"
                      fullWidth
                      color="secondary"
                      inputProps={{ maxLength: 18 }} // CPF: 14 chars, CNPJ: 18 chars
                    />
                  )}
                </CpfCnpjMaskedInput>
              </WrapperInput>
            </Column>
            <Column>
              <WrapperInput>
                <TextField
                  label="Razão Social"
                  name="destinatario_nome_razao_social"
                  value={values.destinatario_nome_razao_social}
                  onChange={handleChange}
                  fullWidth
                  color="secondary"
                />
              </WrapperInput>
            </Column>
          </Row>
          <Row>
            <Column>
              <WrapperInput>
                <TextField
                  label="Inscrição Estadual"
                  name="destinatario_inscricao_estadual"
                  value={values.destinatario_inscricao_estadual}
                  onChange={handleChange}
                  fullWidth
                  color="secondary"
                />
              </WrapperInput>
            </Column>
            <Column>
              <WrapperInput>
                <MaskedInput
                  mask="99999-999"
                  value={values.destinatario_cep}
                  onChange={(e) => {
                    const cepValue = e.target.value;
                    setFieldValue('destinatario_cep', cepValue);
                    
                    // Buscar CEP automaticamente quando estiver completo (8 dígitos)
                    const cepLimpo = cepValue.replace(/\D/g, '');
                    if (cepLimpo.length === 8) {
                      console.log('🔵 CEP completo no onChange (destinatario), chamando buscarCep...');
                      setTimeout(() => {
                        buscarCep(cepValue, setFieldValue, setValues, values, 'destinatario');
                      }, 100);
                    }
                  }}
                  onBlur={(e) => {
                    const cep = e.target.value;
                    console.log('🔵 MaskedInput onBlur chamado para CEP destinatario:', cep);
                    const cepLimpo = cep.replace(/\D/g, '');
                    // Busca automaticamente quando o CEP estiver completo (8 dígitos)
                    if (cepLimpo.length === 8) {
                      console.log('🔵 CEP completo no onBlur (destinatario), chamando buscarCep...');
                      buscarCep(cep, setFieldValue, setValues, values, 'destinatario');
                    }
                  }}
                >
                  {(inputProps) => {
                    // Remover onBlur dos inputProps pois não pode ser passado para o filho
                    const { onBlur, ...restProps } = inputProps;
                    return (
                      <TextField
                        {...restProps}
                        label="CEP"
                        fullWidth
                        color="secondary"
                      />
                    );
                  }}
                </MaskedInput>
              </WrapperInput>
            </Column>
          </Row>
          <Row>
            <Column>
              <WrapperInput>
                <TextField
                  label="Logradouro"
                  name="destinatario_logradouro"
                  value={values.destinatario_logradouro}
                  onChange={handleChange}
                  fullWidth
                  color="secondary"
                />
              </WrapperInput>
            </Column>
            <Column>
              <WrapperInput>
                <TextField
                  label="Número"
                  name="destinatario_numero"
                  value={values.destinatario_numero}
                  onChange={handleChange}
                  fullWidth
                  color="secondary"
                />
              </WrapperInput>
            </Column>
          </Row>
          <Row>
            <Column>
              <WrapperInput>
                <TextField
                  label="Complemento"
                  name="destinatario_complemento"
                  value={values.destinatario_complemento}
                  onChange={handleChange}
                  fullWidth
                  color="secondary"
                />
              </WrapperInput>
            </Column>
            <Column>
              <WrapperInput>
                <TextField
                  label="Bairro"
                  name="destinatario_bairro"
                  value={values.destinatario_bairro}
                  onChange={handleChange}
                  fullWidth
                  color="secondary"
                />
              </WrapperInput>
            </Column>
          </Row>
          <Row>
            <Column>
              <WrapperInput>
                <TextField
                  label="UF"
                  name="destinatario_uf"
                  value={values.destinatario_uf}
                  onChange={handleChange}
                  select
                  fullWidth
                  color="secondary"
                >
                  <option value="">Selecione</option>
                  {UFS_BRASIL.map((uf) => (
                    <option key={uf.value} value={uf.value}>
                      {uf.label}
                    </option>
                  ))}
                </TextField>
              </WrapperInput>
            </Column>
            <Column>
              <WrapperInput>
                <TextField
                  label="Código IBGE Município"
                  name="destinatario_municipio_codigo"
                  value={values.destinatario_municipio_codigo}
                  onChange={handleChange}
                  fullWidth
                  color="secondary"
                />
              </WrapperInput>
            </Column>
          </Row>
          <Row>
            <Column>
              <WrapperInput>
                <TextField
                  label="Município"
                  name="destinatario_municipio_descricao"
                  value={values.destinatario_municipio_descricao}
                  onChange={handleChange}
                  fullWidth
                  color="secondary"
                />
              </WrapperInput>
            </Column>
            <Column>
              <WrapperInput>
                <TextField
                  label="DDD Telefone"
                  name="destinatario_telefone_ddd"
                  value={values.destinatario_telefone_ddd}
                  onChange={handleChange}
                  fullWidth
                  color="secondary"
                />
              </WrapperInput>
            </Column>
          </Row>
          <Row>
            <Column>
              <WrapperInput>
                <TextField
                  label="Telefone"
                  name="destinatario_telefone_numero"
                  value={values.destinatario_telefone_numero}
                  onChange={handleChange}
                  fullWidth
                  color="secondary"
                />
              </WrapperInput>
            </Column>
            <Column>
              <WrapperInput>
                <TextField
                  label="E-mail"
                  name="destinatario_email"
                  type="email"
                  value={values.destinatario_email}
                  onChange={handleChange}
                  fullWidth
                  color="secondary"
                />
              </WrapperInput>
            </Column>
          </Row>
          <Row>
            <Column>
              <WrapperInput>
                <TextField
                  label="Latitude"
                  name="destinatario_lat"
                  value={values.destinatario_lat || ''}
                  onChange={handleChange}
                  fullWidth
                  color="secondary"
                  disabled
                  helperText="Preenchido automaticamente ao buscar o endereço"
                />
              </WrapperInput>
            </Column>
            <Column>
              <WrapperInput>
                <TextField
                  label="Longitude"
                  name="destinatario_lng"
                  value={values.destinatario_lng || ''}
                  onChange={handleChange}
                  fullWidth
                  color="secondary"
                  disabled
                  helperText="Preenchido automaticamente ao buscar o endereço"
                />
              </WrapperInput>
            </Column>
          </Row>
        </>
      )}

      {/* Tab Tomador */}
      {activeTab === 4 && (
        <>
          <SectionTitle>Dados do Tomador do Serviço</SectionTitle>
          <Row>
            <Column>
              <WrapperInput>
                <TextField
                  label="Tipo de Tomador"
                  name="tomador_tipo"
                  value={values.tomador_tipo}
                  onChange={(e) => {
                    const tipoTomador = e.target.value;
                    setFieldValue('tomador_tipo', tipoTomador);
                    
                    // Preencher dados do tomador baseado no tipo selecionado
                    if (tipoTomador === '0' || tipoTomador === '3') {
                      // Remetente ou Destinatário - copiar dados
                      const dadosParaPreencher = preencherTomadorPorTipo(tipoTomador, values);
                      setValues((prevValues) => ({
                        ...prevValues,
                        tomador_tipo: tipoTomador,
                        ...dadosParaPreencher,
                      }));
                    } else {
                      // Expedidor (1) ou Recebedor (2) - limpar campos
                      setValues((prevValues) => ({
                        ...prevValues,
                        tomador_tipo: tipoTomador,
                        tomador_cpf_cnpj: '',
                        tomador_nome_razao_social: '',
                        tomador_inscricao_estadual: '',
                        tomador_cep: '',
                        tomador_logradouro: '',
                        tomador_numero: '',
                        tomador_complemento: '',
                        tomador_bairro: '',
                        tomador_uf: '',
                        tomador_municipio_codigo: '',
                        tomador_municipio_descricao: '',
                        tomador_telefone_ddd: '',
                        tomador_telefone_numero: '',
                        tomador_email: '',
                      }));
                    }
                  }}
                  select
                  fullWidth
                  color="secondary"
                >
                  <option value="0">Remetente</option>
                  <option value="1">Expedidor</option>
                  <option value="2">Recebedor</option>
                  <option value="3">Destinatário</option>
                </TextField>
              </WrapperInput>
            </Column>
            <Column>
              <WrapperInput>
                <CpfCnpjMaskedInput
                  value={values.tomador_cpf_cnpj}
                  onChange={(e) => setFieldValue('tomador_cpf_cnpj', e.target.value)}
                >
                  {(inputProps) => (
                    <TextField
                      {...inputProps}
                      label="CPF/CNPJ"
                      fullWidth
                      color="secondary"
                    />
                  )}
                </CpfCnpjMaskedInput>
              </WrapperInput>
            </Column>
          </Row>
          <Row>
            <Column>
              <WrapperInput>
                <TextField
                  label="Razão Social"
                  name="tomador_nome_razao_social"
                  value={values.tomador_nome_razao_social}
                  onChange={handleChange}
                  fullWidth
                  color="secondary"
                />
              </WrapperInput>
            </Column>
            <Column>
              <WrapperInput>
                <TextField
                  label="Inscrição Estadual"
                  name="tomador_inscricao_estadual"
                  value={values.tomador_inscricao_estadual}
                  onChange={handleChange}
                  fullWidth
                  color="secondary"
                />
              </WrapperInput>
            </Column>
          </Row>
          <Row>
            <Column>
              <WrapperInput>
                <MaskedInput
                  mask="99999-999"
                  value={values.tomador_cep}
                  onChange={(e) => {
                    const cepValue = e.target.value;
                    setFieldValue('tomador_cep', cepValue);
                    
                    // Buscar CEP automaticamente quando estiver completo (8 dígitos)
                    const cepLimpo = cepValue.replace(/\D/g, '');
                    if (cepLimpo.length === 8) {
                      console.log('🔵 CEP completo no onChange (tomador), chamando buscarCep...');
                      setTimeout(() => {
                        buscarCep(cepValue, setFieldValue, setValues, values, 'tomador');
                      }, 100);
                    }
                  }}
                  onBlur={(e) => {
                    const cep = e.target.value;
                    console.log('🔵 MaskedInput onBlur chamado para CEP tomador:', cep);
                    const cepLimpo = cep.replace(/\D/g, '');
                    // Busca automaticamente quando o CEP estiver completo (8 dígitos)
                    if (cepLimpo.length === 8) {
                      console.log('🔵 CEP completo no onBlur (tomador), chamando buscarCep...');
                      buscarCep(cep, setFieldValue, setValues, values, 'tomador');
                    }
                  }}
                >
                  {(inputProps) => {
                    // Remover onBlur dos inputProps pois não pode ser passado para o filho
                    const { onBlur, ...restProps } = inputProps;
                    return (
                      <TextField
                        {...restProps}
                        label="CEP"
                        fullWidth
                        color="secondary"
                      />
                    );
                  }}
                </MaskedInput>
              </WrapperInput>
            </Column>
            <Column>
              <WrapperInput>
                <TextField
                  label="Logradouro"
                  name="tomador_logradouro"
                  value={values.tomador_logradouro}
                  onChange={handleChange}
                  fullWidth
                  color="secondary"
                />
              </WrapperInput>
            </Column>
          </Row>
          <Row>
            <Column>
              <WrapperInput>
                <TextField
                  label="Número"
                  name="tomador_numero"
                  value={values.tomador_numero}
                  onChange={handleChange}
                  fullWidth
                  color="secondary"
                />
              </WrapperInput>
            </Column>
            <Column>
              <WrapperInput>
                <TextField
                  label="Complemento"
                  name="tomador_complemento"
                  value={values.tomador_complemento}
                  onChange={handleChange}
                  fullWidth
                  color="secondary"
                />
              </WrapperInput>
            </Column>
          </Row>
          <Row>
            <Column>
              <WrapperInput>
                <TextField
                  label="Bairro"
                  name="tomador_bairro"
                  value={values.tomador_bairro}
                  onChange={handleChange}
                  fullWidth
                  color="secondary"
                />
              </WrapperInput>
            </Column>
            <Column>
              <WrapperInput>
                <TextField
                  label="UF"
                  name="tomador_uf"
                  value={values.tomador_uf}
                  onChange={handleChange}
                  select
                  fullWidth
                  color="secondary"
                >
                  <option value="">Selecione</option>
                  {UFS_BRASIL.map((uf) => (
                    <option key={uf.value} value={uf.value}>
                      {uf.label}
                    </option>
                  ))}
                </TextField>
              </WrapperInput>
            </Column>
          </Row>
          <Row>
            <Column>
              <WrapperInput>
                <TextField
                  label="Código IBGE Município"
                  name="tomador_municipio_codigo"
                  value={values.tomador_municipio_codigo}
                  onChange={handleChange}
                  fullWidth
                  color="secondary"
                />
              </WrapperInput>
            </Column>
            <Column>
              <WrapperInput>
                <TextField
                  label="Município"
                  name="tomador_municipio_descricao"
                  value={values.tomador_municipio_descricao}
                  onChange={handleChange}
                  fullWidth
                  color="secondary"
                />
              </WrapperInput>
            </Column>
          </Row>
          <Row>
            <Column>
              <WrapperInput>
                <TextField
                  label="DDD Telefone"
                  name="tomador_telefone_ddd"
                  value={values.tomador_telefone_ddd}
                  onChange={handleChange}
                  fullWidth
                  color="secondary"
                />
              </WrapperInput>
            </Column>
            <Column>
              <WrapperInput>
                <TextField
                  label="Telefone"
                  name="tomador_telefone_numero"
                  value={values.tomador_telefone_numero}
                  onChange={handleChange}
                  fullWidth
                  color="secondary"
                />
              </WrapperInput>
            </Column>
          </Row>
          <WrapperInput>
            <TextField
              label="E-mail"
              name="tomador_email"
              type="email"
              value={values.tomador_email}
              onChange={handleChange}
              fullWidth
              color="secondary"
            />
          </WrapperInput>
        </>
      )}

      {/* Tab Transporte */}
      {activeTab === 5 && (
        <>
          <SectionTitle>Dados do Transporte</SectionTitle>
          <Row>
            <Column>
              <WrapperInput>
                <TextField
                  label="Valor do Serviço"
                  name="valor_servico"
                  type="number"
                  value={values.valor_servico || ''}
                  onChange={handleChange}
                  fullWidth
                  color="secondary"
                  inputProps={{ step: '0.01', min: '0' }}
                  InputProps={{
                    startAdornment: <span style={{ marginRight: '8px', color: '#666' }}>R$</span>
                  }}
                />
              </WrapperInput>
            </Column>
            <Column>
              <WrapperInput>
                <TextField
                  label="Valor a Receber"
                  name="valor_receber"
                  type="number"
                  value={values.valor_receber || ''}
                  onChange={handleChange}
                  fullWidth
                  color="secondary"
                  inputProps={{ step: '0.01', min: '0' }}
                  InputProps={{
                    startAdornment: <span style={{ marginRight: '8px', color: '#666' }}>R$</span>
                  }}
                />
              </WrapperInput>
            </Column>
          </Row>
          <Row>
            <Column>
              <WrapperInput>
                <TextField
                  label="Valor da Carga"
                  name="valor_carga"
                  type="number"
                  value={values.valor_carga || ''}
                  onChange={handleChange}
                  fullWidth
                  color="secondary"
                  inputProps={{ step: '0.01', min: '0' }}
                  InputProps={{
                    startAdornment: <span style={{ marginRight: '8px', color: '#666' }}>R$</span>
                  }}
                />
              </WrapperInput>
            </Column>
            <Column>
              <WrapperInput>
                <TextField
                  label="Produtos Predominantes"
                  name="produtos_predominantes"
                  value={values.produtos_predominantes}
                  onChange={handleChange}
                  fullWidth
                  color="secondary"
                />
              </WrapperInput>
            </Column>
          </Row>
          <Row>
            <Column>
              <WrapperInput>
                <TextField
                  label="Chave de Acesso da NFe"
                  name="chave_nfe"
                  value={values.chave_nfe || ''}
                  onChange={handleChange}
                  fullWidth
                  color="secondary"
                  inputProps={{ maxLength: 44 }}
                  helperText="Chave de acesso da NFe (44 dígitos)"
                />
              </WrapperInput>
            </Column>
          </Row>
          <WrapperInput>
            <TextField
              label="Outras Características"
              name="outras_caracteristicas"
              value={values.outras_caracteristicas}
              onChange={handleChange}
              multiline
              rows={4}
              fullWidth
              color="secondary"
            />
          </WrapperInput>
          <WrapperInput>
            <TextField
              label="Informações Adicionais"
              name="informacoes_adicionais"
              value={values.informacoes_adicionais}
              onChange={handleChange}
              multiline
              rows={4}
              fullWidth
              color="secondary"
            />
          </WrapperInput>
          <WrapperInput>
            <TextField
              label="Observações"
              name="observacoes"
              value={values.observacoes}
              onChange={handleChange}
              multiline
              rows={4}
              fullWidth
              color="secondary"
            />
          </WrapperInput>
        </>
      )}

      {/* Tab Motorista */}
      {activeTab === 6 && (
        <>
          <SectionTitle>Dados do Motorista (para MDF-e)</SectionTitle>
          <Row>
            <Column>
              <WrapperInput>
                <TextField
                  label="Selecionar Motorista"
                  name="motorista_id"
                  value={values.motorista_id}
                  onChange={(e) => {
                    const truckerId = e.target.value;
                    const trucker = truckers.find(t => t.id === truckerId);
                    setFieldValue('motorista_id', truckerId);
                    if (trucker) {
                      setFieldValue('motorista_nome', trucker.name || '');
                      setFieldValue('motorista_cpf', trucker.cpf || '');
                      // Extrair telefone - pode estar em campos separados ou combinado
                      const phoneClean = trucker.phone ? trucker.phone.replace(/\D/g, '') : '';
                      const telefoneDdd = phoneClean.length >= 10 ? phoneClean.substring(0, 2) : (phoneClean.length >= 2 ? phoneClean.substring(0, 2) : '');
                      const telefoneNumero = phoneClean.length >= 10 ? phoneClean.substring(2) : (phoneClean.length > 2 ? phoneClean.substring(2) : phoneClean);
                      setFieldValue('motorista_telefone_ddd', telefoneDdd);
                      setFieldValue('motorista_telefone_numero', telefoneNumero);
                      // Preencher dados do veículo se o motorista tiver veículo cadastrado
                      if (trucker.vehicle_plate) {
                        setFieldValue('veiculo_placa', trucker.vehicle_plate || '');
                        setFieldValue('veiculo_renavam', trucker.vehicle_renavam || '');
                        setFieldValue('veiculo_proprietario_cpf_cnpj', trucker.vehicle_owner_cpf_cnpj || '');
                        setFieldValue('veiculo_proprietario_nome', trucker.vehicle_owner_name || '');
                      }
                    }
                  }}
                  select
                  fullWidth
                  color="secondary"
                >
                  <option value="">Selecione um motorista</option>
                  {truckers.map((trucker) => (
                    <option key={trucker.id} value={trucker.id}>
                      {trucker.name} - {trucker.cpf}
                    </option>
                  ))}
                </TextField>
              </WrapperInput>
            </Column>
          </Row>
          <Row>
            <Column>
              <WrapperInput>
                <TextField
                  label="Nome do Motorista"
                  name="motorista_nome"
                  value={values.motorista_nome}
                  onChange={handleChange}
                  fullWidth
                  color="secondary"
                />
              </WrapperInput>
            </Column>
            <Column>
              <WrapperInput>
                <MaskedInput
                  mask="999.999.999-99"
                  value={values.motorista_cpf}
                  onChange={(e) => setFieldValue('motorista_cpf', e.target.value)}
                >
                  {(inputProps) => (
                    <TextField
                      {...inputProps}
                      label="CPF do Motorista"
                      fullWidth
                      color="secondary"
                    />
                  )}
                </MaskedInput>
              </WrapperInput>
            </Column>
          </Row>
          <Row>
            <Column>
              <WrapperInput>
                <TextField
                  label="DDD Telefone"
                  name="motorista_telefone_ddd"
                  value={values.motorista_telefone_ddd}
                  onChange={handleChange}
                  fullWidth
                  color="secondary"
                  inputProps={{ maxLength: 2 }}
                />
              </WrapperInput>
            </Column>
            <Column>
              <WrapperInput>
                <MaskedInput
                  mask="99999-9999"
                  value={values.motorista_telefone_numero}
                  onChange={(e) => setFieldValue('motorista_telefone_numero', e.target.value)}
                >
                  {(inputProps) => (
                    <TextField
                      {...inputProps}
                      label="Telefone"
                      fullWidth
                      color="secondary"
                    />
                  )}
                </MaskedInput>
              </WrapperInput>
            </Column>
          </Row>
        </>
      )}

      {/* Tab Veículo */}
      {activeTab === 7 && (
        <>
          <SectionTitle>Dados do Veículo</SectionTitle>
          <Row>
            <Column>
              <WrapperInput>
                <TextField
                  label="Placa"
                  name="veiculo_placa"
                  value={values.veiculo_placa}
                  onChange={handleChange}
                  fullWidth
                  color="secondary"
                  inputProps={{ maxLength: 7, style: { textTransform: 'uppercase' } }}
                />
              </WrapperInput>
            </Column>
            <Column>
              <WrapperInput>
                <TextField
                  label="RENAVAM"
                  name="veiculo_renavam"
                  value={values.veiculo_renavam}
                  onChange={handleChange}
                  fullWidth
                  color="secondary"
                />
              </WrapperInput>
            </Column>
          </Row>
          <Row>
            <Column>
              <WrapperInput>
                <TextField
                  label="UF do Veículo"
                  name="veiculo_uf"
                  value={values.veiculo_uf}
                  onChange={handleChange}
                  select
                  fullWidth
                  color="secondary"
                >
                  <option value="">Selecione</option>
                  {UFS_BRASIL.map((uf) => (
                    <option key={uf.value} value={uf.value}>
                      {uf.label}
                    </option>
                  ))}
                </TextField>
              </WrapperInput>
            </Column>
            <Column>
              <WrapperInput>
                <CpfCnpjMaskedInput
                  value={values.veiculo_proprietario_cpf_cnpj}
                  onChange={(e) => setFieldValue('veiculo_proprietario_cpf_cnpj', e.target.value)}
                >
                  {(inputProps) => (
                    <TextField
                      {...inputProps}
                      label="CPF/CNPJ do Proprietário"
                      fullWidth
                      color="secondary"
                    />
                  )}
                </CpfCnpjMaskedInput>
              </WrapperInput>
            </Column>
          </Row>
          <Row>
            <Column>
              <WrapperInput>
                <TextField
                  label="Nome do Proprietário"
                  name="veiculo_proprietario_nome"
                  value={values.veiculo_proprietario_nome}
                  onChange={handleChange}
                  fullWidth
                  color="secondary"
                />
              </WrapperInput>
            </Column>
            <Column>
              <WrapperInput>
                <TextField
                  label="Inscrição Estadual do Proprietário"
                  name="veiculo_proprietario_inscricao_estadual"
                  value={values.veiculo_proprietario_inscricao_estadual}
                  onChange={handleChange}
                  fullWidth
                  color="secondary"
                />
              </WrapperInput>
            </Column>
          </Row>
          <Row>
            <Column>
              <WrapperInput>
                <TextField
                  label="UF do Proprietário"
                  name="veiculo_proprietario_uf"
                  value={values.veiculo_proprietario_uf}
                  onChange={handleChange}
                  select
                  fullWidth
                  color="secondary"
                >
                  <option value="">Selecione</option>
                  {UFS_BRASIL.map((uf) => (
                    <option key={uf.value} value={uf.value}>
                      {uf.label}
                    </option>
                  ))}
                </TextField>
              </WrapperInput>
            </Column>
          </Row>
        </>
      )}

    </>
  );
}

export default ViagemForm;

